import { Component, OnInit, ViewChild, Output, EventEmitter, Input, AfterViewInit } from '@angular/core';
import { Moment } from 'moment';
import { AvailabilityModel } from 'src/app/providers/availability.provider';
import * as moment from 'moment';
import { MatDateRangePicker, DateRange } from '@angular/material/datepicker';
import Utils from 'src/app/shared/utils';
import { FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_LOCALE, MAT_DATE_FORMATS } from '@angular/material/core';

export interface DateRangePickerValue {
  start: Moment;
  end: Moment;
}

export const MY_FORMATS = {
  parse: {
    dateInput: 'LL',
  },
  display: {
    dateInput: 'DD MMM YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'YYYY',
  },
};

const today = moment().startOf('day');

@Component({
  selector: 'app-date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },

    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class DateRangePickerComponent implements OnInit, AfterViewInit {
  @Input() getAvailabilityForPeriod: (start: Moment, end: Moment) => Promise<AvailabilityModel[]>;

  @Output() change = new EventEmitter<DateRangePickerValue>();

  @ViewChild(MatDateRangePicker) datepicker: MatDateRangePicker<Moment>;

  private loadingAvailability = false;
  private displayedMonth: Moment;
  private availabilityCache: { [yearMonth: string]: AvailabilityModel[] } = {};

  private isNotAvailable = (date: Moment): boolean => {
    const isOnDisplayedMonth = date.month() === this.displayedMonth.month();

    if (isOnDisplayedMonth) {
      return !this.dateFilter(date);
    } else {
      return !this.isDateAvailable(date);
    }
  };

  public formGroup = new FormGroup(
    {
      start: new FormControl(),
      end: new FormControl(),
    },
    [dateValidator(this.isNotAvailable), periodLengthValidator]
  );

  public numberOfNights: number;

  ngOnInit() {
    this.formGroup.valueChanges.subscribe(() => {
      // This is a workaround to the fact that <mat-error>s inside a
      // <mat-form-field> are only displayed when a formControl <input>
      // inside has validation errors, not when <mat-date-range-input>
      // has errors -- and so here I’m artificially forwarding the error
      // state to the "start" <input>.
      this.formGroup.controls.start.setErrors(this.formGroup.errors);

      this.emitChange();
    });
  }

  ngAfterViewInit() {
    this.initDatepicker();
  }

  private initDatepicker() {
    let datepickerMonth = today.clone().startOf('month');

    this.datepicker.openedStream.subscribe(async () => {
      // Even if openedStream emits, datepicker’s DOM is not actually
      // rendered yet, so we wait before reading the selected month. :-|
      await this.waitForDatepickerDom();

      // Navigating to the year-view is not helpful; would just create
      // confusion.
      this.disablePeriodButton();
      this.previousMonthButton.disabled = true;

      await this.displayMonthAvailability(datepickerMonth);

      this.previousMonthButton.addEventListener('click', () => {
        datepickerMonth = datepickerMonth.clone().subtract(1, 'month');
        if (datepickerMonth.month() == today.month()) {
          this.previousMonthButton.disabled = true;
        }
        this.displayMonthAvailability(datepickerMonth);
      });

      this.nextMonthButton.addEventListener('click', () => {
        this.previousMonthButton.disabled = false;
        datepickerMonth = datepickerMonth.clone().add(1, 'month');
        this.displayMonthAvailability(datepickerMonth);
      });
    });
  }

  private disablePeriodButton(): void {
    const periodButton = this.headerControls.querySelector('.mat-calendar-period-button') as HTMLButtonElement;
    const arrow = periodButton.querySelector('.mat-calendar-arrow') as HTMLElement;

    periodButton.disabled = true;
    arrow.style.display = 'none';
  }

  public emitChange() {
    if (this.formGroup.invalid) {
      this.change.emit(undefined);
      return;
    }

    const value = this.formGroup.value as DateRange<Moment>;
    let { start, end } = value;

    if (!start || !end) {
      // If any of them is missing, we’re not talking.
      return;
    }

    if (end.isBefore(start)) {
      // This happens when I select a range, and then click to start
      // another, and I click later in the calendar, which means I have
      // now the start later than the end, which is still from the
      // previous selection. And so here I’m ensuring that start comes
      // before end.
      [start, end] = [end, start];
    }

    const emittedValue: DateRangePickerValue = {
      start,
      end,
    };

    this.setNumberOfNights(value);
    this.change.emit(emittedValue);
  }

  private setNumberOfNights(value: DateRange<Moment>): void {
    this.numberOfNights = value.end.diff(value.start, 'days');
  }

  private dateFilterNaySayer(date: Moment) {
    return false;
  }

  private dateFilterByAvailability(date: Moment) {
    if (this.loadingAvailability) {
      return false;
    }

    if (date.isBefore(today)) {
      return false;
    }

    return this.isDateAvailable(date);
  }

  private isDateAvailable(date: Moment): boolean {
    const month = date.clone().startOf('month');
    const monthAvailability = this.getCachedAvailabiltiyForMonth(month);

    if (!monthAvailability) {
      return false;
    }

    const isDateAvailable = monthAvailability.some((a) => date.isSame(a.day));

    return isDateAvailable;
  }

  public dateFilter = this.dateFilterNaySayer;

  private async displayMonthAvailability(month: Moment): Promise<void> {
    this.displayedMonth = month;

    await this.loadAvailabilityForDisplayedMonth();
    await this.updateDatepicker();
  }

  private getCachedAvailabiltiyForMonth(month: Moment): AvailabilityModel[] {
    return this.availabilityCache[this.getAvailabilityCacheKey(month)];
  }

  private cacheAvailability(month: Moment, availability: AvailabilityModel[]): void {
    this.availabilityCache[this.getAvailabilityCacheKey(month)] = availability;
  }

  private getAvailabilityCacheKey(startOfMonth: Moment) {
    return startOfMonth.format('YYYY-MM');
  }

  private async waitForDatepickerDom(): Promise<void> {
    // This is empirically determined.
    const millisecondsToWait = 1;

    await Utils.sleep(millisecondsToWait);
  }

  private async loadAvailabilityForDisplayedMonth(): Promise<void> {
    const month = this.displayedMonth;
    const cachedAvailability = this.getCachedAvailabiltiyForMonth(month);

    if (cachedAvailability) {
      return;
    }

    const startOfMonth = month.clone().startOf('month');
    const endOfMonth = month.clone().endOf('month');
    const availability = await this.loadAvailabilityForPeriod(startOfMonth, endOfMonth);

    this.cacheAvailability(month, availability);
  }

  private loadAvailabilityForPeriod(start: Moment, end: Moment): Promise<AvailabilityModel[]> {
    this.loadingAvailability = true;

    return this.getAvailabilityForPeriod(start, end).finally(() => (this.loadingAvailability = false));
  }

  private async updateDatepicker() {
    // Since there is no API for refreshing the <mat-datepicker>, we’re
    // resetting its `matDatepickerFilter` here to cause it to update.
    this.dateFilter = (date: Moment) => this.dateFilterByAvailability(date);
  }

  private get nextMonthButton(): HTMLButtonElement {
    return this.getDatepickerMonthButton('next');
  }

  private get previousMonthButton(): HTMLButtonElement {
    return this.getDatepickerMonthButton('previous');
  }

  private getDatepickerMonthButton(type: 'next' | 'previous'): HTMLButtonElement {
    return this.headerControls.querySelector(`.mat-calendar-${type}-button`);
  }

  private get headerControls(): HTMLDivElement {
    return this.domElement.querySelector('.mat-calendar-controls');
  }

  private get domElement(): HTMLElement {
    return document.querySelector('mat-datepicker-content');
  }
}

function dateValidator(isNotAvailable: (m: Moment) => boolean) {
  return (range: FormGroup): ValidationErrors | null => {
    const value = range.value as DateRange<Moment>;

    if (!value.start || !value.end) {
      return;
    }

    const dayCount = Math.abs(value.end.diff(value.start, 'days')) + 1;
    const selectedDays = Array(dayCount)
      .fill(null)
      .map((_null, i) => value.start.clone().add(i, 'days'));

    const unavailableDates = selectedDays.filter(isNotAvailable);

    if (unavailableDates.length > 0) {
      return { unavailableDates: unavailableDates.map((m) => m.format('MMM D')).join(', ') };
    }
  };
}

function periodLengthValidator(range: FormGroup): ValidationErrors {
  const value = range.value as DateRange<Moment>;

  if (value.start && value.end && value.end.isSame(value.start)) {
    return { periodLength: true };
  }
}
